import { useEffect } from 'react';

export type SheetRow = {
  id: string
  contact_name?: string
  organization?: string
  role?: string
  public_business_email?: string
  public_business_phone?: string
  city?: string
  state_or_region?: string
  segment?: string
  record_status?: string
  score?: number
  source_url?: string
  last_verified_at?: string
};

const COLUMNS: { key: keyof SheetRow; label: string }[] = [
  { key: 'organization', label: 'Organization' },
  { key: 'contact_name', label: 'Contact' },
  { key: 'role', label: 'Role' },
  { key: 'public_business_email', label: 'Email' },
  { key: 'public_business_phone', label: 'Phone' },
  { key: 'city', label: 'City' },
  { key: 'state_or_region', label: 'State' },
  { key: 'segment', label: 'Matched signal' },
  { key: 'record_status', label: 'Status' },
  { key: 'score', label: 'Score' },
  { key: 'source_url', label: 'Source' },
  { key: 'last_verified_at', label: 'Verified' },
];

const csvCell = (value: unknown) => {
  const text = value === undefined || value === null ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** CSV with the same columns the sheet shows. */
export const contactsToCsv = (rows: SheetRow[]) => [
  COLUMNS.map((column) => column.label).join(','),
  ...rows.map((row) => COLUMNS.map((column) => csvCell(row[column.key])).join(',')),
].join('\n');

const fileSlug = (name: string) => (name || 'contacts').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'contacts';

export const downloadContactsCsv = (rows: SheetRow[], businessName = '') => {
  const blob = new Blob([contactsToCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileSlug(businessName)}-contacts.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const STATUS_STYLE: Record<string, string> = {
  active: 'bg-[#E7F6EC] text-[#1E7B3C]',
  uncertain: 'bg-[#FFF4DB] text-[#8A5A00]',
  candidate: 'bg-[#EAF1FB] text-[#2A5DA8]',
};

export const StatusChip = ({ status }: { status?: string }) => (
  <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[status || ''] || 'bg-[#EEF2F4] text-[#3F5B64]'}`}>
    {status === 'uncertain' ? 'needs review' : status || 'unknown'}
  </span>
);

type ContactSheetModalProps = {
  open: boolean
  onClose: () => void
  rows: SheetRow[]
  businessName?: string
  airtableUrl?: string
};

/** Spreadsheet view of the generated list with a CSV download. */
const ContactSheetModal = ({ open, onClose, rows, businessName, airtableUrl }: ContactSheetModalProps) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label="Contacts view" data-testid="contact-sheet-modal" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-6xl flex-col rounded-2xl bg-white shadow-xl font-open-sans" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 border-b border-[#E4EEF1] px-5 py-4">
          <div>
            <p className="page-kicker">Contacts view</p>
            <h3 className="font-raleway font-semibold text-ink">{businessName ? `${businessName} contact list` : 'Contact list'}</h3>
            <div className="mt-1 flex items-center gap-3 text-xs text-[#3F5B64]">
              <span>{rows.length} {rows.length === 1 ? 'row' : 'rows'} generated in HomeCook</span>
              <button type="button" className="font-semibold text-primaryDark-0 hover:underline disabled:opacity-50" disabled={rows.length === 0} onClick={() => downloadContactsCsv(rows, businessName)}>
                Download CSV
              </button>
            </div>
          </div>
          <button type="button" className="rounded-lg border border-[#E4EEF1] px-3 py-2 text-sm" onClick={onClose} aria-label="Close contacts view">Close</button>
        </div>
        <div className="overflow-auto">
          {rows.length === 0 ? (
            <p className="p-5 text-sm text-[#3F5B64]">No contacts have been generated for this business yet.</p>
          ) : (
            <table className="min-w-full border-collapse text-left text-xs" data-testid="contact-sheet">
              <thead className="sticky top-0 bg-[#F6FAFB]">
                <tr>
                  <th className="border-b border-r border-[#E4EEF1] px-2 py-2 text-[#3F5B64]">#</th>
                  {COLUMNS.map((column) => (
                    <th key={column.key} className="whitespace-nowrap border-b border-r border-[#E4EEF1] px-3 py-2 font-semibold text-ink">{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} className="odd:bg-white even:bg-[#FBFDFD]">
                    <td className="border-b border-r border-[#E4EEF1] px-2 py-2 text-[#3F5B64]">{index + 1}</td>
                    {COLUMNS.map((column) => {
                      const value = row[column.key];
                      if (column.key === 'record_status') {
                        return <td key={column.key} className="border-b border-r border-[#E4EEF1] px-3 py-2"><StatusChip status={row.record_status} /></td>;
                      }
                      if (column.key === 'source_url' && value) {
                        return (
                          <td key={column.key} className="max-w-[220px] truncate border-b border-r border-[#E4EEF1] px-3 py-2">
                            <a href={String(value)} target="_blank" rel="noreferrer" className="text-primaryDark-0 underline">{String(value)}</a>
                          </td>
                        );
                      }
                      return <td key={column.key} className="max-w-[220px] truncate border-b border-r border-[#E4EEF1] px-3 py-2">{value === undefined || value === '' ? '—' : String(value)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {airtableUrl && (
          <div className="border-t border-[#E4EEF1] px-5 py-3 text-xs text-[#3F5B64]">
            A list already built in DSCVRY stays in the Business Hub.{' '}
            <a href={airtableUrl} target="_blank" rel="noreferrer" className="font-semibold text-primaryDark-0">Open it in DSCVRY</a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSheetModal;
